import type { TFunction } from 'i18next';

/**
 * POS-I276: the "Phone" column and drawer field of the admin list.
 * Active now (one or more live sessions) / Last seen N min|h|days ago / Never opened.
 * Pure, so the wording thresholds are tested without a screen.
 */
export interface PhoneInfo {
  liveSessions?: number | null;
  lastSeenAt?: string | null;
}

export type PhoneStatus =
  | { kind: 'active'; count: number }
  | { kind: 'seen'; key: 'phoneSeenNow' | 'phoneSeenMin' | 'phoneSeenHour' | 'phoneSeenDay'; count: number }
  | { kind: 'never' };

export function phoneStatus(row: PhoneInfo, now: number = Date.now()): PhoneStatus {
  if ((row.liveSessions ?? 0) > 0) return { kind: 'active', count: row.liveSessions as number };
  if (!row.lastSeenAt) return { kind: 'never' };
  const seconds = Math.max(0, Math.floor((now - new Date(row.lastSeenAt).getTime()) / 1000));
  if (seconds < 60) return { kind: 'seen', key: 'phoneSeenNow', count: 0 };
  if (seconds < 3600) return { kind: 'seen', key: 'phoneSeenMin', count: Math.floor(seconds / 60) };
  if (seconds < 86400) return { kind: 'seen', key: 'phoneSeenHour', count: Math.floor(seconds / 3600) };
  return { kind: 'seen', key: 'phoneSeenDay', count: Math.floor(seconds / 86400) };
}

/** Text of a status (the active case is the caller's badge; this returns its count line). */
export function phoneText(t: TFunction, status: PhoneStatus): string {
  if (status.kind === 'active') return t('phoneActiveCount', { count: status.count });
  if (status.kind === 'never') return t('phoneNever');
  return t(status.key, { count: status.count });
}
